/*eslint-disable max-lines*/
import { useUpdateDispatchTimestamp } from '@hakimo-ui/hakimo/data-access';
import {
  DataVersion,
  LocationAlarmUpdate,
  LocationAlarmUpdateType,
  ModifyAlarmUpdateEntry,
  ScanAlarmUpdateType,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { Tooltip } from '@hakimo-ui/shared/ui-base';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {
  ComponentType,
  ReactNode,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAlarmGroupStatic } from '../alarm-provider/AlarmGroupProvider';
import {
  EditState,
  ResponderType,
  SubCategory,
  derivedUpdateType,
} from '../../sop-workflow/util';
import { displayTime } from '../alarm-updates/util';
import {
  DispatchTimelineRowCompact,
  DispatchTimelineRowProps,
} from './DispatchTimelineRow';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface DispatchTimelineEditorHandle {
  flushUpdates: (rollbackOnError?: boolean) => Promise<boolean>;
}

interface Props {
  events: LocationAlarmUpdate[];
  timezone?: string;
  alarmId: string;
  product: UnifiedMonitoringProduct;
  onEditingChange?: (editing: boolean) => void;
  action?: ReactNode;
  /** Row chrome. Defaults to the compact operator-escalation row. */
  Row?: ComponentType<DispatchTimelineRowProps>;
  /** Keep the list inside a fixed-height scroller. */
  scrollable?: boolean;
  /** Rendered in place of the list when there are no events. Without it
   *  the whole component renders nothing, as it always has. */
  emptyState?: ReactNode;
  titleClassName?: string;
  /** Overrides the alarm-group context, which callers outside an
   *  AlarmGroupProvider (e.g. the incident details page) do not have. */
  dataVersion?: DataVersion;
}

export const DispatchTimelineEditor = forwardRef<
  DispatchTimelineEditorHandle,
  Props
>(function DispatchTimelineEditor(
  {
    events,
    timezone: tz,
    alarmId,
    product,
    onEditingChange,
    action,
    Row = DispatchTimelineRowCompact,
    scrollable = true,
    emptyState,
    titleClassName = 'text-sm',
    dataVersion: dataVersionProp,
  },
  ref
) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState | null>(null);
  const [pending, setPending] = useState<
    Record<string, Partial<ModifyAlarmUpdateEntry>>
  >({});
  const [pendingFiles, setPendingFiles] = useState<Record<string, File[]>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { dataVersion: contextDataVersion } = useAlarmGroupStatic();
  const dataVersion = dataVersionProp ?? contextDataVersion;
  const { mutateAsync } = useUpdateDispatchTimestamp(alarmId, {
    product,
    dataVersion:
      product === UnifiedMonitoringProduct.SCAN ? undefined : dataVersion,
  });

  const sorted = useMemo(
    () =>
      [...events].sort(
        (a, b) =>
          dayjs(displayTime(a, pending[String(a.id)])).valueOf() -
          dayjs(displayTime(b, pending[String(b.id)])).valueOf()
      ),
    [events, pending]
  );

  useImperativeHandle(ref, () => ({
    flushUpdates: async (rollbackOnError = false) => {
      const entries = Object.entries(pending).map(([id, p]) => ({
        alarm_update_id: id,
        ...p,
      }));
      if (!entries.length) return false;
      const fd = new FormData();
      entries.forEach((entry, i) => {
        const payload: Record<string, unknown> = {
          alarm_update_id: entry.alarm_update_id,
          ...(entry.update_timestamp_utc != null && {
            update_timestamp_utc: entry.update_timestamp_utc,
          }),
          ...(entry.update_type != null && { update_type: entry.update_type }),
          ...(entry.dispatch_status != null && {
            dispatch_status: entry.dispatch_status,
          }),
          ...(entry.responder_type != null && {
            responder_type: entry.responder_type,
          }),
          ...(entry.comment != null && { comment: entry.comment }),
        };
        if (entry.s3_urls !== undefined) {
          payload['s3_urls'] = entry.s3_urls;
        }
        fd.append(`entries[${i}]`, JSON.stringify(payload));
        (pendingFiles[entry.alarm_update_id] ?? []).forEach((f, j) =>
          fd.append(`entries[${i}][snapshot][${j}]`, f)
        );
      });
      try {
        await mutateAsync(fd);
      } catch (err) {
        if (rollbackOnError) {
          setPending({});
          setPendingFiles({});
        }
        throw err;
      }
      setPending({});
      setPendingFiles({});
      return true;
    },
  }));

  const patch = (p: Partial<EditState>) =>
    setEditState((prev) => (prev ? { ...prev, ...p } : prev));

  const startEdit = (ev: LocationAlarmUpdate) => {
    if (editingId !== null) return;

    const id = String(ev.id);
    const p = pending[id] ?? {};

    const preLoadedFiles = pendingFiles[id] ?? [];
    const preLoadedBlobUrls = preLoadedFiles.map((f) => URL.createObjectURL(f));

    setEditState({
      updateType: derivedUpdateType(
        p.update_type ?? (ev.update_type as string)
      ),
      dispatchStatus: (p.dispatch_status ?? ev.dispatch_status ?? '') as
        | SubCategory
        | '',
      responderType: (p.responder_type ?? ev.responder_type ?? '') as
        | ResponderType
        | '',
      comment:
        p.comment ?? ev.escalation_comment ?? ev.comment ?? ev.notes ?? '',
      tsIso: null,
      s3Urls: p.s3_urls ?? ev.s3_urls ?? [],
      pendingFiles: preLoadedFiles,
      pendingBlobUrls: preLoadedBlobUrls,
      previewIdx: null,
      imagesModified: preLoadedFiles.length > 0,
    });
    setEditingId(id);
    onEditingChange?.(true);
  };

  const clearEditState = () => {
    setEditState(null);
    setEditingId(null);
    onEditingChange?.(false);
  };

  const cancelEdit = () => {
    if (editState) {
      editState.pendingBlobUrls.forEach((u) => URL.revokeObjectURL(u));
    }
    clearEditState();
  };

  const saveEdit = (ev: LocationAlarmUpdate) => {
    const id = String(ev.id);
    if (!editState) return;
    const s = editState;
    const p: Partial<ModifyAlarmUpdateEntry> = {};

    if (s.tsIso !== null) p.update_timestamp_utc = s.tsIso;

    const prevType = derivedUpdateType(ev.update_type as string);
    if (s.updateType !== prevType) {
      const ut = s.updateType;
      p.update_type = (
        product === UnifiedMonitoringProduct.SCAN
          ? ut === 'evidence'
            ? ScanAlarmUpdateType.COMMENT_FOR_SNAPSHOT
            : ut
          : {
              dispatch: LocationAlarmUpdateType.DISPATCH,
              arrival: LocationAlarmUpdateType.ARRIVAL,
              evidence: LocationAlarmUpdateType.ESCALATION_SNAPSHOT_COMMENT,
            }[ut]
      ) as ModifyAlarmUpdateEntry['update_type'];
    }

    const prevResponder = ev.responder_type ?? '';
    if (s.updateType !== 'evidence' && s.responderType !== prevResponder)
      p.responder_type =
        s.responderType as ModifyAlarmUpdateEntry['responder_type'];

    const prevStatus = ev.dispatch_status ?? '';
    if (
      s.updateType === 'dispatch' &&
      s.dispatchStatus &&
      s.dispatchStatus !== prevStatus
    )
      p.dispatch_status =
        s.dispatchStatus as ModifyAlarmUpdateEntry['dispatch_status'];

    if (s.comment !== (ev.escalation_comment ?? ev.comment ?? ev.notes ?? ''))
      p.comment = s.comment;

    if (s.imagesModified) {
      p.s3_urls = s.s3Urls;
      if (s.pendingFiles.length) {
        setPendingFiles((prev) => ({ ...prev, [id]: s.pendingFiles }));
      } else {
        setPendingFiles(({ [id]: _, ...rest }) => rest);
      }
    }

    if (Object.keys(p).length)
      setPending((prev) => ({ ...prev, [id]: { ...(prev[id] ?? {}), ...p } }));
    clearEditState();
  };

  if (!sorted.length && !emptyState) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 pt-2">
        <div className="flex items-center gap-1.5">
          <span
            className={clsx(
              'text-onlight-text-1 dark:text-ondark-text-1 font-semibold',
              titleClassName
            )}
          >
            Verify &amp; Edit Timeline
          </span>
          <span className="text-onlight-text-2 dark:text-ondark-text-1 dark:bg-ondark-bg-3 bg-onlight-bg-2 ml-1 rounded-full px-2 py-0.5 text-xs font-medium">
            {sorted.length} {sorted.length === 1 ? 'Event' : 'Events'}
          </span>
          {tz && (
            <Tooltip text={`TZ - ${tz}`} position="right" nowrap>
              <InformationCircleIcon className="text-onlight-text-2 dark:text-ondark-text-2 h-4 w-4" />
            </Tooltip>
          )}
        </div>
        {action}
      </div>

      <div
        className={clsx(
          scrollable ? 'max-h-96 overflow-y-auto pl-1 pr-3' : 'pt-2'
        )}
      >
        {sorted.length === 0 && emptyState}
        {sorted.map((ev, index) => {
          const id = String(ev.id);
          const inEdit = editingId === id;
          return (
            <Row
              key={id}
              ev={ev}
              pending={pending[id] ?? {}}
              pendingFiles={pendingFiles[id] ?? []}
              inEdit={inEdit}
              editState={inEdit ? editState : null}
              isLast={index === sorted.length - 1}
              timezone={tz}
              fileInputRef={fileInputRef}
              editDisabled={editingId !== null && !inEdit}
              onEdit={() => startEdit(ev)}
              onSave={() => saveEdit(ev)}
              onCancel={cancelEdit}
              patch={patch}
            />
          );
        })}
      </div>
    </div>
  );
});
